import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Anime from 'react-anime';
import BackToHomeBtn from './BackToHomeBtn';
import { projectsData, getProjectBySlug, getProjectSlug } from '../data/data';
import StatsSection from './StatsSection';
import '../styles/ProjectsPage.css';
import '../styles/ProjectsTopBar.css';

const ProjectDetail = ({ project }) => (
  <div className="project-item">
    <div className="project-heading">
      <h2>{project.name}</h2>
      <p>{project.heading}</p>
      <h3>Tech Stack: {project.tech}</h3>
    </div>
    <div className="thumb">
      <img src={project.thumb} alt={project.heading} />
    </div>
    <div className="project-links">
      {project.links.live && (
        <a className="link" target="_blank" href={project.links.live} rel="noopener noreferrer">
          Live Demo
        </a>
      )}
      {project.links.repo && (
        <a className="link" target="_blank" href={project.links.repo} rel="noopener noreferrer">
          Code on GitHub
        </a>
      )}
    </div>
    <div className="project-details">
      <p>{project.details}</p>
    </div>
  </div>
);

const ProjectModal = ({ project, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div className="project-modal-overlay" onClick={onClose}>
      <div className="project-modal" onClick={(event) => event.stopPropagation()}>
        <button type="button" className="project-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>
        <ProjectDetail project={project} />
      </div>
    </div>
  );
};

const truncateText = (text, maxLength) =>
  text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;

const ProjectsList = ({ projects, onSelectProject }) => (
  <div className="projects-grid">
    {projects.map((project) => (
      <button
        type="button"
        className="project-card"
        key={getProjectSlug(project.name)}
        onClick={() => onSelectProject(project)}
      >
        <div className="project-card-image">
          <img src={project.thumb} alt={project.name} />
        </div>
        <div className="project-card-body">
          <h3 className="project-card-title">{project.name}</h3>
          <p className="project-card-excerpt">{truncateText(project.heading, 90)}</p>
          <div className="project-card-tags">
            {project.tech.split('/').map((tag) => (
              <span key={tag} className="project-card-tag">
                {tag.trim()}
              </span>
            ))}
          </div>
        </div>
      </button>
    ))}
  </div>
);

const ProjectsPage = ({ returnBtn, initialTab = 'projects' }) => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const activeTab = initialTab === 'stats' ? 'stats' : 'projects';
  const [hovered, setHovered] = useState(null);
  const selectedProject = slug ? getProjectBySlug(slug) : null;

  useEffect(() => {
    if (slug && !selectedProject) {
      navigate('/projects', { replace: true });
    }
  }, [slug, selectedProject, navigate]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .custom-scroll::-webkit-scrollbar {
        height: 4px;
        background: transparent;
        border-radius: 4px;
      }
      .custom-scroll::-webkit-scrollbar-thumb {
        background: white;
        border-radius: 20px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleSelectProject = (project) => {
    navigate(`/projects/${getProjectSlug(project.name)}`, { replace: !!slug });
  };

  const handleCloseProject = () => {
    navigate(activeTab === 'stats' ? '/stats' : '/projects', { replace: true });
  };

  const handleTabChange = (tab) => {
    const path = tab === 'stats' ? '/stats' : '/projects';
    navigate(path, { replace: true });
  };

  return (
    <div
      style={{
        overflow: 'hidden',
        position: 'fixed',
        inset: 0,
        backgroundColor: '#F3F8F8',
      }}
    >
      <Anime easing="easeOutCubic" opacity={[0, 1]} translateY={-30} duration={400} delay={300}>
        <div className="projects-page">
          <div className="topnav">
            <div
              className="topnav button-container"
              onMouseLeave={() => setHovered(null)}
            >
              <div
                className={`button ${activeTab === 'projects' && hovered === null ? 'active' : ''}`}
                onClick={() => handleTabChange('projects')}
                onMouseEnter={() => setHovered('projects')}
              >
                PROJECTS
              </div>

              <div
                className={`button ${activeTab === 'stats' && hovered === null ? 'active' : ''}`}
                onClick={() => handleTabChange('stats')}
                onMouseEnter={() => setHovered('stats')}
              >
                STATS
              </div>
            </div>
          </div>

          <div className="projects-back-btn">
            <BackToHomeBtn handleClick={returnBtn} />
          </div>

          <div className="about-scroll projects-scroll">
            <div className="page-content">
                {activeTab === 'stats' ? (
                  <StatsSection />
                ) : (
                  <div className="projects-section">
                    <h2>Projects</h2>
                    <div className="projects-container">
                      <ProjectsList
                        projects={projectsData}
                        onSelectProject={handleSelectProject}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
        </div>
      </Anime>

      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={handleCloseProject} />
      )}
    </div>
  );
};

export default ProjectsPage;
